/**
 * Upper bounds on a template's periodization — how many blocks a program has, and how many weeks
 * sit inside each block.
 *
 * These mirror `AdminNewTemplateService.MAX_BLOCKS` / `MAX_WEEKS_IN_BLOCK` on the server, which
 * rejects anything outside the range with a 400. Keep the two in sync: the server is the real
 * guard, and this exists so a trainer finds out while typing rather than after pressing save.
 *
 * WHY THE CAP EXISTS: `numberOfBlocks x numberOfWeeksInBlock` is expanded eagerly into real rows
 * when a program is assigned from the template — a 40x40 template becomes 1,600 weeks, each with
 * workouts, exercises and interval rows beneath it. A handful of templates set to extreme values
 * produced program trees large enough to dominate the database and to exceed the archiver's payload
 * ceiling. 20x20 = 400 weeks is far past any real training plan (the deepest week anyone had
 * actually trained to was 5) while leaving the tree a manageable size.
 */
export const MAX_BLOCKS = 20;
export const MAX_WEEKS_IN_BLOCK = 20;
export const MIN_BLOCKS = 1;
export const MIN_WEEKS_IN_BLOCK = 1;

/** Shown under a periodization field once the typed value is out of range. */
export const PERIODIZATION_ERROR_TEXT = `יש להזין ערך בין ${MIN_BLOCKS} ל-${MAX_BLOCKS}`;

/**
 * Shown the moment a typed value is clamped down.
 *
 * Without this the field silently rewrites 40 to 20 as you type, which reads like a broken input.
 * The message names the limit so the correction is understandable rather than mysterious.
 */
export const maxExceededText = (max: number): string =>
  `הערך המרבי הוא ${max} — הערך שהוזן קוצר ל-${max}`;

/**
 * Clamp a periodization field to its allowed range.
 *
 * Deliberately NOT relying on the `max` attribute of `<input type="number">`. That attribute only
 * constrains the spinner arrows — a value typed or pasted straight into the field sails past it,
 * the input stays visually valid, and the form submits. `ImportTemplatePage` carried `max: 12` for
 * exactly this purpose and oversized templates were created through it anyway, which is how the
 * program trees this cap addresses came to exist.
 *
 * A non-numeric or empty input yields `min` rather than `NaN`, matching the previous
 * `parseInt(...) || 1` behaviour at every call site.
 */
export const clampPeriodization = (value: unknown, max: number, min = 1): number => {
  const parsed = parseInt(String(value), 10);
  if (Number.isNaN(parsed)) {
    return min;
  }
  return Math.min(Math.max(parsed, min), max);
};
